import { useContext, useEffect, useState } from 'react'

import {
  Drawer,
  TextInput,
  Select,
  NumberInput,
  Divider,
  Text,
  Tooltip,
  Collapse,
  ActionIcon,
  Alert,
  Switch,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { useDisclosure } from '@mantine/hooks'
import { ChevronDown, ChevronRight, Info, AlertCircle } from 'lucide-react'
import { zodResolver } from 'mantine-form-zod-resolver'
import { z } from 'zod'

import { useQuery, useMutation } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import Button from 'src/components/Buttons/Button/Button'
import PhoneInput from 'src/components/Inputs/PhoneInput/PhoneInput'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { useApplicantDrawerContext } from 'src/context/ApplicantDrawerContext'
import { RecruitersContext } from 'src/context/RecruitersContext'
import {
  GET_APPLICANTS,
  CREATE_APPLICANT,
} from 'src/graphql/applicants.graphql'
import { GET_JOB_LISTINGS } from 'src/graphql/joblistings.graphql'
import {
  ApplicantStage,
  HiringStage,
  NotInterestedReason,
  DisqualifiedReason,
  TerminatedReason,
} from 'src/graphql/types/applicants'
import { JobStatus } from 'src/graphql/types/jobListings'
import { formatSnakeValue } from 'src/lib/formatters'
import { formatLeadSourceLabel } from 'src/lib/leadSource.utils'
import { buildMutationErrorMessage } from 'src/lib/mutationErrorMessage'
import { TERMINATED_REASON_OPTIONS } from 'src/lib/terminatedReason.utils'
import {
  removeCountryCode,
  unformatPhoneNumber,
  usDigits,
} from 'src/lib/phone.utils'

interface CreateApplicantDrawerProps {
  opened: boolean
  close: () => void
  prefilledValues?: {
    name?: string
    phone?: string
    email?: string
  } | null
}

const schema = z.object({
  firstName: z
    .string()
    .min(2, { message: 'First name should have at least 2 letters' })
    .max(20, { message: 'First name should have at most 20 letters' }),
  middleName: z
    .string()
    .min(0)
    .max(20, { message: 'Middle name should have at most 20 letters' }),
  lastName: z
    .string()
    .min(2, { message: 'Last name should have at least 2 letters' })
    .max(20, { message: 'Last name should have at most 20 letters' }),
  email: z
    .string()
    .trim()
    .transform((val) => (val === '' ? undefined : val))
    .optional()
    .refine((val) => !val || z.string().email().safeParse(val).success, {
      message: 'Invalid email',
    }),
  phone: z
    .string()
    .refine(
      (data) => unformatPhoneNumber(removeCountryCode(data)).length === 10,
      {
        message: 'Phone number should have 10 digits',
      }
    ),
  yearsOfExperience: z.number().min(0).max(50).optional(),
  zipCode: z.string().max(10).optional(),
  jobListingId: z.string().min(1, { message: 'Job listing is required' }),
  trackingLinkId: z.string().min(1, { message: 'Tracking link is required' }),
  recruiterId: z.string().nullish(),
  applicantStage: z.nativeEnum(ApplicantStage),
  hiringStage: z.nativeEnum(HiringStage).nullish(),
  notInterestedReason: z.nativeEnum(NotInterestedReason).nullish(),
  disqualifiedReason: z.nativeEnum(DisqualifiedReason).nullish(),
  terminatedReason: z.nativeEnum(TerminatedReason).nullish(),
  automationsEnabled: z.boolean(),
})

const applicantStageOptions = Object.keys(ApplicantStage).map((stage) => ({
  value: stage,
  label: formatSnakeValue(stage),
}))

const hiringStageOptions = Object.keys(HiringStage).map((stage) => ({
  value: stage,
  label: formatSnakeValue(stage),
}))

const notInterestedReasonOptions = Object.keys(NotInterestedReason).map(
  (reason) => ({
    value: reason,
    label: formatSnakeValue(reason),
  })
)

const disqualifiedReasonOptions = Object.keys(DisqualifiedReason).map(
  (reason) => ({
    value: reason,
    label: formatSnakeValue(reason),
  })
)

const terminatedReasonOptions = TERMINATED_REASON_OPTIONS

const JOB_LISTINGS_PAGE_LIMIT = 500

const CreateApplicantDrawer = ({
  opened,
  close,
  prefilledValues = null,
}: CreateApplicantDrawerProps) => {
  const [trackingLinks, setTrackingLinks] = useState([])
  const [hasInitializedRecruiter, setHasInitializedRecruiter] = useState(false)
  const [stageOpened, { toggle: toggleStage }] = useDisclosure(false)
  const [createdApplicantId, setCreatedApplicantId] = useState<string | null>(
    null
  )
  const { currentUser } = useAuth()
  const currentEmployeeId = currentUser?.currentEmployeeId
  const recruiters = useContext(RecruitersContext) || []
  const { handleOpen: openApplicantDrawer } = useApplicantDrawerContext()

  const form = useForm({
    initialValues: {
      firstName: '',
      middleName: '',
      lastName: '',
      email: '',
      phone: '',
      yearsOfExperience: undefined,
      zipCode: '',
      jobListingId: null,
      trackingLinkId: null,
      recruiterId: null,
      applicantStage: ApplicantStage.PROSPECT,
      hiringStage: HiringStage.LEAD,
      notInterestedReason: null,
      disqualifiedReason: null,
      terminatedReason: null,
      automationsEnabled: true,
    },
    validate: zodResolver(schema),
  })

  const { data: jobListingsResponse, loading: jobListingsLoading } = useQuery(
    GET_JOB_LISTINGS,
    {
      variables: {
        pageInfo: { offset: 0, limit: JOB_LISTINGS_PAGE_LIMIT },
        filters: {
          statuses: [JobStatus.ACTIVE],
        },
      },
      onError: () => {
        toast('Unable to get job listings, please try again.', 'error')
      },
    }
  )

  const jobListingOptions =
    jobListingsResponse?.jobListings.items?.map((jl) => ({
      value: jl.jobListingId,
      label: jl.title,
    })) || []

  const [addApplicant, { loading: loadingAddApplicant }] = useMutation(
    CREATE_APPLICANT,
    {
      refetchQueries: [GET_APPLICANTS],
      onCompleted: (data) => {
        toast('Applicant created successfully!', 'success')
        setCreatedApplicantId(data.createApplicant.applicantId)
      },
      onError: (error) => {
        toast(
          buildMutationErrorMessage(
            error,
            'Unable to create applicant, please try again.'
          ),
          'error'
        )
      },
    }
  )

  const handleSubmit = (values) => {
    const { applicantStage } = values

    const input = {
      jobListingId: values.jobListingId,
      firstName: values.firstName,
      ...(values.middleName && { middleName: values.middleName }),
      lastName: values.lastName,
      ...(values.email && { email: values.email }),
      phone: unformatPhoneNumber(values.phone),
      yearsOfExperience: values.yearsOfExperience,
      ...(values.zipCode && { zipCode: values.zipCode }),
      trackingLinkId: values.trackingLinkId,
      ...(values.recruiterId ? { recruiterId: values.recruiterId } : {}),
      applicantStage,
      // Only include hiringStage for PROSPECT
      ...(applicantStage === ApplicantStage.PROSPECT &&
        values.hiringStage && { hiringStage: values.hiringStage }),
      // Only include sub-reasons for their respective stages
      ...(applicantStage === ApplicantStage.NOT_INTERESTED &&
        values.notInterestedReason && {
          notInterestedReason: values.notInterestedReason,
        }),
      ...(applicantStage === ApplicantStage.DISQUALIFIED &&
        values.disqualifiedReason && {
          disqualifiedReason: values.disqualifiedReason,
        }),
      ...(applicantStage === ApplicantStage.TERMINATED &&
        values.terminatedReason && {
          terminatedReason: values.terminatedReason,
        }),
      automationsEnabled: values.automationsEnabled,
    }
    addApplicant({ variables: { input } })
  }

  const handleViewProfile = () => {
    if (createdApplicantId) {
      openApplicantDrawer(createdApplicantId)
    }
    handleClose()
  }

  const handleClose = () => {
    form.reset()
    setCreatedApplicantId(null)
    close()
  }

  // Update tracking links when job listing changes
  useEffect(() => {
    form.setFieldValue('trackingLinkId', null)
    if (form.values.jobListingId && jobListingsResponse?.jobListings?.items) {
      const jobListing = jobListingsResponse.jobListings.items.find(
        (jl) => jl.jobListingId === form.values.jobListingId
      )
      const links = jobListing?.trackingLinks.map((link) => ({
        value: link.trackingLinkId,
        label: formatLeadSourceLabel(link) ?? '',
      }))
      setTrackingLinks(links || [])
    } else {
      setTrackingLinks([])
    }
  }, [form.values.jobListingId, jobListingsResponse])

  // Reset recruiter initialization when drawer opens
  useEffect(() => {
    if (!opened) {
      return
    }
    setHasInitializedRecruiter(false)
  }, [opened])

  // Auto-populate recruiter if current user is a recruiter
  useEffect(() => {
    if (hasInitializedRecruiter || recruiters.length === 0) {
      return
    }

    if (
      !form.values.recruiterId &&
      currentEmployeeId &&
      recruiters.some((recruiter) => recruiter.value === currentEmployeeId)
    ) {
      form.setFieldValue('recruiterId', currentEmployeeId)
    }

    setHasInitializedRecruiter(true)
  }, [
    recruiters,
    currentEmployeeId,
    form,
    hasInitializedRecruiter,
    form.values.recruiterId,
  ])

  // Handle prefilled values when drawer opens
  useEffect(() => {
    if (opened && prefilledValues) {
      const phone = prefilledValues.phone || ''
      const formattedPhone =
        phone && !phone.startsWith('+')
          ? usDigits(phone)
            ? `+1${usDigits(phone)}`
            : ''
          : phone
      if (phone) {
        form.setFieldValue('phone', formattedPhone)
      }
      if (prefilledValues.email) {
        form.setFieldValue('email', prefilledValues.email)
      }
      if (prefilledValues.name) {
        form.setFieldValue('firstName', prefilledValues.name)
      }
    }
  }, [opened, prefilledValues])

  // Reset form when drawer closes
  useEffect(() => {
    if (!opened) {
      form.reset()
      setTrackingLinks([])
      setCreatedApplicantId(null)
    }
  }, [opened])

  // Clear sub-reason when applicant stage changes
  useEffect(() => {
    const { applicantStage } = form.values
    if (applicantStage === ApplicantStage.PROSPECT) {
      form.setFieldValue('notInterestedReason', null)
      form.setFieldValue('disqualifiedReason', null)
      form.setFieldValue('terminatedReason', null)
      if (!form.values.hiringStage) {
        form.setFieldValue('hiringStage', HiringStage.LEAD)
      }
    } else if (applicantStage === ApplicantStage.NOT_INTERESTED) {
      form.setFieldValue('hiringStage', null)
      form.setFieldValue('disqualifiedReason', null)
      form.setFieldValue('terminatedReason', null)
    } else if (applicantStage === ApplicantStage.DISQUALIFIED) {
      form.setFieldValue('hiringStage', null)
      form.setFieldValue('notInterestedReason', null)
      form.setFieldValue('terminatedReason', null)
    } else if (applicantStage === ApplicantStage.TERMINATED) {
      form.setFieldValue('hiringStage', null)
      form.setFieldValue('notInterestedReason', null)
      form.setFieldValue('disqualifiedReason', null)
    } else if (applicantStage === ApplicantStage.HIRED) {
      form.setFieldValue('hiringStage', null)
      form.setFieldValue('notInterestedReason', null)
      form.setFieldValue('disqualifiedReason', null)
      form.setFieldValue('terminatedReason', null)
    }
  }, [form.values.applicantStage])

  const renderSubReasonSelect = () => {
    const { applicantStage } = form.values

    if (applicantStage === ApplicantStage.PROSPECT) {
      return (
        <div className="flex flex-row items-center justify-between">
          <Text size="xs">Hiring Stage</Text>
          <div className="w-[250px]">
            <Select
              placeholder="Select hiring stage"
              size="xs"
              data={hiringStageOptions}
              classNames={{
                input: 'text-xs text-right',
              }}
              {...form.getInputProps('hiringStage')}
            />
          </div>
        </div>
      )
    }

    if (applicantStage === ApplicantStage.NOT_INTERESTED) {
      return (
        <div className="flex flex-row items-center justify-between">
          <Text size="xs">Reason</Text>
          <div className="w-[250px]">
            <Select
              placeholder="Select reason (optional)"
              size="xs"
              data={notInterestedReasonOptions}
              classNames={{
                input: 'text-xs text-right',
              }}
              clearable
              {...form.getInputProps('notInterestedReason')}
            />
          </div>
        </div>
      )
    }

    if (applicantStage === ApplicantStage.DISQUALIFIED) {
      return (
        <div className="flex flex-row items-center justify-between">
          <Text size="xs">Reason</Text>
          <div className="w-[250px]">
            <Select
              placeholder="Select reason (optional)"
              size="xs"
              data={disqualifiedReasonOptions}
              classNames={{
                input: 'text-xs text-right',
              }}
              clearable
              {...form.getInputProps('disqualifiedReason')}
            />
          </div>
        </div>
      )
    }

    if (applicantStage === ApplicantStage.TERMINATED) {
      return (
        <div className="flex flex-row items-center justify-between">
          <Text size="xs">Reason</Text>
          <div className="w-[250px]">
            <Select
              placeholder="Select reason (optional)"
              size="xs"
              data={terminatedReasonOptions}
              classNames={{
                input: 'text-xs text-right',
              }}
              clearable
              {...form.getInputProps('terminatedReason')}
            />
          </div>
        </div>
      )
    }

    return null
  }

  return (
    <Drawer
      opened={opened}
      onClose={handleClose}
      position="right"
      withCloseButton={false}
      size="30%"
      offset={6}
      radius="md"
      stackId="create-applicant-drawer"
      overlayProps={{ backgroundOpacity: 0.2 }}
      classNames={{
        title: 'text-base font-semibold text-doubleNickel-gray-900',
        body: 'flex flex-col gap-10 h-full',
      }}
    >
      {createdApplicantId ? (
        <div className="flex h-full flex-col gap-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-doubleNickel-gray-900">
              Applicant Created
            </h2>
          </div>

          <Divider variant="dashed" />

          <div className="flex flex-1 flex-col gap-4">
            <Text size="sm" className="text-doubleNickel-gray-700">
              The applicant has been successfully added to the system.
            </Text>

            <Alert
              icon={<AlertCircle className="h-4 w-4" />}
              color="yellow"
              variant="light"
            >
              <Text size="xs">
                If this is an existing applicant in the system, the stage you
                entered may not be reflected. The system preserves the
                applicant&apos;s current stage to maintain data integrity.
              </Text>
            </Alert>

            <div className="mt-auto flex flex-col gap-2">
              <Button
                text="View Profile"
                variant="primary"
                onClick={handleViewProfile}
                fullWidth
              />
              <Button
                text="Close"
                variant="outline"
                onClick={handleClose}
                fullWidth
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="flex h-full flex-col gap-4">
          <div className="flex h-full flex-1 flex-col gap-3 overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-doubleNickel-gray-900">
                Create Applicant
              </h2>
              <Button
                text="Create"
              variant="primary"
              onClick={form.onSubmit(handleSubmit)}
              loading={loadingAddApplicant}
              size="sm"
            />
          </div>

          <Divider variant="dashed" />

          {/* Form Content */}
          <div className="flex flex-col gap-4 overflow-y-auto">
            {/* Section 1: Personal Information */}
            <div className="space-y-4">
              <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
                PERSONAL INFORMATION
              </Text>
              <div className="space-y-3">
                <div className="flex flex-row items-center justify-between">
                  <Text size="xs">
                    First Name
                    <span className="text-doubleNickel-error-500">{' *'}</span>
                  </Text>
                  <div className="w-[250px]">
                    <TextInput
                      placeholder="Type first name"
                      size="xs"
                      classNames={{
                        input: 'text-xs text-right',
                      }}
                      {...form.getInputProps('firstName')}
                    />
                  </div>
                </div>

                <div className="flex flex-row items-center justify-between">
                  <Text size="xs">Middle Name</Text>
                  <div className="w-[250px]">
                    <TextInput
                      placeholder="Type middle name"
                      size="xs"
                      classNames={{
                        input: 'text-xs text-right',
                      }}
                      {...form.getInputProps('middleName')}
                    />
                  </div>
                </div>

                <div className="flex flex-row items-center justify-between">
                  <Text size="xs">
                    Last Name
                    <span className="text-doubleNickel-error-500">{' *'}</span>
                  </Text>
                  <div className="w-[250px]">
                    <TextInput
                      placeholder="Type last name"
                      size="xs"
                      classNames={{
                        input: 'text-xs text-right',
                      }}
                      {...form.getInputProps('lastName')}
                    />
                  </div>
                </div>

                <div className="flex flex-row items-center justify-between">
                  <Text size="xs">
                    Phone Number
                    <span className="text-doubleNickel-error-500">{' *'}</span>
                  </Text>
                  <div className="w-[250px]">
                    <PhoneInput
                      size="xs"
                      classNames={{
                        input: 'text-xs text-right',
                      }}
                      {...form.getInputProps('phone')}
                    />
                  </div>
                </div>

                <div className="flex flex-row items-center justify-between">
                  <Text size="xs">E-mail</Text>
                  <div className="w-[250px]">
                    <TextInput
                      placeholder="Type email"
                      size="xs"
                      classNames={{
                        input: 'text-xs text-right',
                      }}
                      {...form.getInputProps('email')}
                    />
                  </div>
                </div>

                <div className="flex flex-row items-center justify-between">
                  <Text size="xs">Experience</Text>
                  <div className="w-[250px]">
                    <NumberInput
                      placeholder="Years of experience"
                      size="xs"
                      allowDecimal={true}
                      allowNegative={false}
                      decimalScale={1}
                      hideControls
                      classNames={{
                        input: 'text-xs text-right',
                      }}
                      {...form.getInputProps('yearsOfExperience')}
                    />
                  </div>
                </div>

                <div className="flex flex-row items-center justify-between">
                  <Text size="xs">Postal Code</Text>
                  <div className="w-[250px]">
                    <TextInput
                      placeholder="Type postal code"
                      size="xs"
                      classNames={{
                        input: 'text-xs text-right',
                      }}
                      {...form.getInputProps('zipCode')}
                    />
                  </div>
                </div>
              </div>
            </div>

            <Divider variant="dashed" />

            {/* Section 2: Job Assignment */}
            <div className="space-y-4">
              <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
                JOB ASSIGNMENT
              </Text>
              <div className="space-y-3">
                <div className="flex flex-row items-center justify-between">
                  <Text size="xs">
                    Job Listing
                    <span className="text-doubleNickel-error-500">{' *'}</span>
                  </Text>
                  <div className="w-[250px]">
                    <Tooltip
                      label="No job listings were found"
                      disabled={jobListingOptions?.length > 0}
                    >
                      <Select
                        placeholder="Select a job listing"
                        size="xs"
                        data={jobListingOptions}
                        searchable
                        disabled={jobListingsLoading}
                        classNames={{
                          input: 'text-xs text-right',
                        }}
                        {...form.getInputProps('jobListingId')}
                      />
                    </Tooltip>
                  </div>
                </div>

                <div className="flex flex-row items-center justify-between">
                  <Text size="xs">
                    Tracking Link
                    <span className="text-doubleNickel-error-500">{' *'}</span>
                  </Text>
                  <div className="w-[250px]">
                    <Select
                      key={String(form.values.trackingLinkId)}
                      placeholder="Select a tracking link"
                      size="xs"
                      data={trackingLinks}
                      searchable
                      disabled={trackingLinks.length === 0}
                      classNames={{
                        input: 'text-xs text-right',
                      }}
                      {...form.getInputProps('trackingLinkId')}
                    />
                  </div>
                </div>

                <div className="flex flex-row items-center justify-between">
                  <div className="flex items-center gap-1">
                    <Text size="xs">Lead Assignment</Text>
                    <Tooltip
                      label="If not selected, round robin assignment will be used based on the job listing configuration."
                      multiline
                      maw={250}
                      withArrow
                    >
                      <div className="flex cursor-help items-center">
                        <Info className="h-3.5 w-3.5 stroke-doubleNickel-gray-400" />
                      </div>
                    </Tooltip>
                  </div>
                  <div className="w-[250px]">
                    <Select
                      placeholder="Select an employee"
                      size="xs"
                      data={recruiters}
                      searchable
                      clearable
                      disabled={recruiters.length === 0}
                      classNames={{
                        input: 'text-xs text-right',
                      }}
                      {...form.getInputProps('recruiterId')}
                    />
                  </div>
                </div>
              </div>
            </div>

            <Divider variant="dashed" />

            {/* Section 3: Stage (Collapsible) */}
            <div className="space-y-4">
              <div
                className="flex cursor-pointer items-center justify-between"
                onClick={toggleStage}
              >
                <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
                  STAGE
                </Text>
                <ActionIcon variant="subtle" color="gray" size={24}>
                  {stageOpened ? (
                    <ChevronDown className="h-4 w-4 text-doubleNickel-gray-500" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-doubleNickel-gray-500" />
                  )}
                </ActionIcon>
              </div>

              <Collapse in={stageOpened}>
                <div className="space-y-3">
                  <div className="flex flex-row items-center justify-between">
                    <Text size="xs">Applicant Stage</Text>
                    <div className="w-[250px]">
                      <Select
                        placeholder="Select applicant stage"
                        size="xs"
                        data={applicantStageOptions}
                        classNames={{
                          input: 'text-xs text-right',
                        }}
                        {...form.getInputProps('applicantStage')}
                      />
                    </div>
                  </div>

                  {renderSubReasonSelect()}
                </div>
              </Collapse>
            </div>

            <Divider variant="dashed" />

            {/* Section 4: Automations */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
                  AUTOMATIONS
                </Text>
                <Tooltip
                  label="When enabled, automated calls, texts, and emails will be sent to this applicant. Turn off to skip automations."
                  multiline
                  maw={250}
                  withArrow
                >
                  <div className="flex cursor-help items-center">
                    <Info className="h-3.5 w-3.5 stroke-doubleNickel-gray-400" />
                  </div>
                </Tooltip>
              </div>
              <Switch
                size="xs"
                {...form.getInputProps('automationsEnabled', { type: 'checkbox' })}
              />
            </div>
          </div>
        </div>
      </div>
      )}
    </Drawer>
  )
}

export default CreateApplicantDrawer
